import { type MessageKey, useTranslation } from '@eluve/i18n';
import { ERRORS, type ExtensionErrorName } from '@eluve/vendor';

export type HumanFriendlyError = {
  title: string;
  message: string;
};

type HumanFriendlyErrorKeys = {
  titleKey: MessageKey;
  messageKey: MessageKey;
};

export const SYNC_ERRORS_TO_HUMAN_FRIENDLY_ERRORS: Record<
  ExtensionErrorName,
  HumanFriendlyErrorKeys
> = {
  HOME_SCREEN_NOT_FOUND: {
    titleKey: 'errors.homeScreenNotFound.title',
    messageKey: 'errors.homeScreenNotFound.message',
  },
  NOT_LOGGED_IN: {
    titleKey: 'errors.notLoggedIn.title',
    messageKey: 'errors.notLoggedIn.message',
  },
  SOAP_TEMPLATE_NOT_FOUND: {
    titleKey: 'errors.soapTemplateNotFound.title',
    messageKey: 'errors.soapTemplateNotFound.message',
  },
  NOTE_EDITOR_NOT_FOUND: {
    titleKey: 'errors.noteEditorNotFound.title',
    messageKey: 'errors.noteEditorNotFound.message',
  },
  TEMPLATE_NOT_SUPPORTED: {
    titleKey: 'errors.templateNotSupported.title',
    messageKey: 'errors.templateNotSupported.message',
  },
  FAILED_TO_SIGN: {
    titleKey: 'errors.failedToSign.title',
    messageKey: 'errors.failedToSign.message',
  },
  GENERIC_ERROR: {
    titleKey: 'errors.genericError.title',
    messageKey: 'errors.genericError.message',
  },
  TIMEOUT: {
    titleKey: 'errors.timeout.title',
    messageKey: 'errors.timeout.message',
  },
  FAILED_TO_IMPORT: {
    titleKey: 'errors.failedToImport.title',
    messageKey: 'errors.failedToImport.message',
  },
  STAFF_MISMATCH: {
    titleKey: 'errors.staffMismatch.title',
    messageKey: 'errors.staffMismatch.message',
  },
  INVALID_STATE_PAGE_REFRESH_REQUIRED: {
    titleKey: 'errors.invalidStatePageRefreshRequired.title',
    messageKey: 'errors.invalidStatePageRefreshRequired.message',
  },
  MIC_DISCONNECTED: {
    titleKey: 'errors.micDisconnected.title',
    messageKey: 'errors.micDisconnected.message',
  },
  MIC_PERMISSIONS_DENIED: {
    titleKey: 'errors.micPermissionsDenied.title',
    messageKey: 'errors.micPermissionsDenied.message',
  },
  DOM_INJECTION_FAILED: {
    titleKey: 'errors.domInjectionFailed.title',
    messageKey: 'errors.domInjectionFailed.message',
  },
  PATIENT_DELETED: {
    titleKey: 'errors.patientDeleted.title',
    messageKey: 'errors.patientDeleted.message',
  },
  APPOINTMENT_NOT_FOUND: {
    titleKey: 'errors.appointmentNotFound.title',
    messageKey: 'errors.appointmentNotFound.message',
  },
  USER_ID_MISMATCH: {
    titleKey: 'errors.userIdMismatch.title',
    messageKey: 'errors.userIdMismatch.message',
  },
  EXTENSION_CONNECTION_LOST: {
    titleKey: 'errors.extensionConnectionLost.title',
    messageKey: 'errors.extensionConnectionLost.message',
  },
  INSUFFICIENT_STORAGE: {
    titleKey: 'errors.insufficientStorage.title',
    messageKey: 'errors.insufficientStorage.message',
  },
  APPOINTMENT_ALREADY_SIGNED: {
    titleKey: 'errors.appointmentAlreadySigned.title',
    messageKey: 'errors.appointmentAlreadySigned.message',
  },
} as const;

const EHR_INTEGRATION_NOT_AVAILABLE_ERROR_KEYS: HumanFriendlyErrorKeys = {
  titleKey: 'errors.ehrIntegrationNotAvailable.title',
  messageKey: 'errors.ehrIntegrationNotAvailable.message',
};

const getHumanFriendlyErrorKeys = (
  error?: ExtensionErrorName | string,
): HumanFriendlyErrorKeys => {
  if (error && error in ERRORS) {
    return SYNC_ERRORS_TO_HUMAN_FRIENDLY_ERRORS[error as ExtensionErrorName];
  }
  if (
    error?.includes('Extension context invalidated') ||
    error?.includes('Lost connection')
  ) {
    return SYNC_ERRORS_TO_HUMAN_FRIENDLY_ERRORS.EXTENSION_CONNECTION_LOST;
  }
  return SYNC_ERRORS_TO_HUMAN_FRIENDLY_ERRORS.GENERIC_ERROR;
};

/**
 * Returns the {@link HumanFriendlyErrorKeys} lookup-table entry the helper
 * would map `error` to. Useful for reference-equality checks like
 * `getHumanFriendlyError(x) === SYNC_ERRORS_TO_HUMAN_FRIENDLY_ERRORS.GENERIC_ERROR`
 * to detect the unmapped-fallback case.
 */
export const getHumanFriendlyError = getHumanFriendlyErrorKeys;

/**
 * Returns a function that maps an extension error name (or arbitrary error
 * string) to a translated `{title, message}` pair in the active UI language.
 * Falls back to English when no `<I18nProvider>` is mounted.
 *
 * @example
 *   const toHumanFriendly = useHumanFriendlyError();
 *   toHumanFriendly(ERRORS.MIC_DISCONNECTED); // → { title: 'Microphone not detected', ... }
 */
export const useHumanFriendlyError = () => {
  const { t } = useTranslation();
  return (error?: ExtensionErrorName | string): HumanFriendlyError => {
    const keys = getHumanFriendlyErrorKeys(error);
    return {
      title: t(keys.titleKey),
      message: t(keys.messageKey),
    };
  };
};

/**
 * Translated `EHR_INTEGRATION_NOT_AVAILABLE` payload — used by the EHR-sync
 * hook to construct its "vendor wiring missing" response without going
 * through `getHumanFriendlyError`.
 */
export const useEhrIntegrationNotAvailableError = (): HumanFriendlyError => {
  const { t } = useTranslation();
  return {
    title: t(EHR_INTEGRATION_NOT_AVAILABLE_ERROR_KEYS.titleKey),
    message: t(EHR_INTEGRATION_NOT_AVAILABLE_ERROR_KEYS.messageKey),
  };
};
